import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  useFDReportsSummary,
  useFDReportsOutcomes,
  useFDReportsProgress,
  useFDReportsServiceNeeds,
  useFDReportsExport,
} from '../hooks/useFoodDistribution';
import {
  getFDReportsSummary,
  getFDReportsOutcomes,
  getFDReportsProgress,
  getFDReportsServiceNeeds,
  getFDReportsExport,
} from '../services/foodDistributionService';
import type { FDClientProgressRecord } from '../types/foodDistribution';
import { exportToCsv, csvFilenameDate } from '../utils/csvExport';
import '../styles/FoodDistribution.css';

type TabId = 'detail' | 'progress' | 'operations' | 'outcomes' | 'service-needs';

function TabButton({
  label,
  active,
  onClick,
}: {
  id: TabId;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button className={`fd-tab${active ? ' active' : ''}`} onClick={onClick}>
      {label}
    </button>
  );
}

// ─── Detail Tab ───────────────────────────────────────────────────────────────

interface FDExportVisit {
  fdVisitId: number;
  fdClientId: number;
  clientUid: string;
  firstName: string;
  lastName: string;
  zipCode: string;
  householdSize: number;
  childrenInHousehold: number;
  tefapEligibility: string | null;
  visitDatetime: string;
  visitSequence: number;
  distributionType: string;
  usdaHh3: string | null;
  usdaHh4: string | null;
  usdaAd1: string | null;
  usdaAd1a: number | null;
  usdaAd1aDontknow: boolean;
  usdaAd2: string | null;
  usdaAd3: string | null;
  usda6RawScore: number | null;
  usda6Category: string | null;
  fruitvegDays: number | null;
  fruitvegDontknow: boolean;
  serviceNeeds: string | null;
  staffUser: string | null;
  notes: string | null;
}

const PAGE_SIZE = 25;

function parseServiceNeeds(raw: string | null): string {
  if (!raw) {
    return '';
  }
  // Handle both JSON arrays (legacy) and comma-separated strings (from STRING_AGG)
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) {
      return arr.join(', ');
    }
  } catch {
    // not JSON, treat as plain string (comma-separated from SQL)
  }
  return raw;
}

function DetailTab({ params }: { params: { startDate?: string; endDate?: string } }) {
  const { data: rawData, isLoading, error } = useFDReportsExport(params, true);
  const [page, setPage] = useState(0);
  const [sortCol, setSortCol] = useState<'visitDatetime' | 'clientUid' | 'distributionType'>(
    'visitDatetime'
  );
  const [sortAsc, setSortAsc] = useState(false);

  const records: FDExportVisit[] = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = rawData as any;
    const list = raw?.records ?? raw;
    return Array.isArray(list) ? list : [];
  }, [rawData]);

  const sorted = useMemo(() => {
    const copy = [...records];
    copy.sort((a, b) => {
      const aVal = a[sortCol] ?? '';
      const bVal = b[sortCol] ?? '';
      if (aVal < bVal) {
        return sortAsc ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortAsc ? 1 : -1;
      }
      return 0;
    });
    return copy;
  }, [records, sortCol, sortAsc]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRecords = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSort = (col: typeof sortCol) => {
    if (col === sortCol) {
      setSortAsc(!sortAsc);
    } else {
      setSortCol(col);
      setSortAsc(true);
    }
    setPage(0);
  };

  const sortIndicator = (col: typeof sortCol) => (sortCol === col ? (sortAsc ? ' ▲' : ' ▼') : '');

  if (isLoading) {
    return <p style={{ color: '#888' }}>Loading visit detail data...</p>;
  }
  if (error) {
    return <p style={{ color: '#c62828' }}>Failed to load visit detail data.</p>;
  }
  if (records.length === 0) {
    return <p style={{ color: '#aaa' }}>No visit records for the selected period.</p>;
  }

  return (
    <div>
      <p style={{ fontSize: '0.8125rem', color: '#888', marginBottom: '0.75rem' }}>
        {records.length} visit record{records.length !== 1 ? 's' : ''} — page {page + 1} of{' '}
        {totalPages}
      </p>

      <div className="fd-table-wrap">
        <table className="fd-table">
          <thead>
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('visitDatetime')}>
                Date{sortIndicator('visitDatetime')}
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('clientUid')}>
                Client ID{sortIndicator('clientUid')}
              </th>
              <th>Client Name</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('distributionType')}>
                Type{sortIndicator('distributionType')}
              </th>
              <th>USDA Score</th>
              <th>Category</th>
              <th>Fruit/Veg Days</th>
              <th>Service Needs</th>
              <th>Staff</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {pageRecords.map((r) => (
              <tr key={r.fdVisitId}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {r.visitDatetime ? new Date(r.visitDatetime).toLocaleDateString() : '—'}
                </td>
                <td>{r.clientUid || r.fdClientId}</td>
                <td>
                  {r.firstName} {r.lastName}
                </td>
                <td>{r.distributionType || '—'}</td>
                <td style={{ textAlign: 'center' }}>{r.usda6RawScore ?? '—'}</td>
                <td>{r.usda6Category || '—'}</td>
                <td style={{ textAlign: 'center' }}>{r.fruitvegDays ?? '—'}</td>
                <td
                  style={{
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {parseServiceNeeds(r.serviceNeeds)}
                </td>
                <td>{r.staffUser || '—'}</td>
                <td
                  style={{
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r.notes || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div
          style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}
        >
          <button
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            style={{
              padding: '0.4rem 0.75rem',
              border: '1px solid #ddd',
              borderRadius: 5,
              background: 'white',
              cursor: page === 0 ? 'default' : 'pointer',
              opacity: page === 0 ? 0.4 : 1,
            }}
          >
            Previous
          </button>
          <span style={{ lineHeight: '2rem', fontSize: '0.875rem', color: '#666' }}>
            {page + 1} / {totalPages}
          </span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
            style={{
              padding: '0.4rem 0.75rem',
              border: '1px solid #ddd',
              borderRadius: 5,
              background: 'white',
              cursor: page >= totalPages - 1 ? 'default' : 'pointer',
              opacity: page >= totalPages - 1 ? 0.4 : 1,
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Progress Tab ─────────────────────────────────────────────────────────────

function ProgressTab({ params }: { params: { startDate?: string; endDate?: string } }) {
  const { data: records, isLoading, error } = useFDReportsProgress(params);
  const [page, setPage] = useState(0);
  const [sortCol, setSortCol] = useState<keyof FDClientProgressRecord>('lastName');
  const [sortAsc, setSortAsc] = useState(true);

  const rows: FDClientProgressRecord[] = useMemo(() => {
    return Array.isArray(records) ? records : [];
  }, [records]);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const aVal = a[sortCol] ?? '';
      const bVal = b[sortCol] ?? '';
      if (aVal < bVal) {
        return sortAsc ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortAsc ? 1 : -1;
      }
      return 0;
    });
    return copy;
  }, [rows, sortCol, sortAsc]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRecords = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSort = (col: keyof FDClientProgressRecord) => {
    if (col === sortCol) {
      setSortAsc(!sortAsc);
    } else {
      setSortCol(col);
      setSortAsc(true);
    }
    setPage(0);
  };

  const sortIndicator = (col: keyof FDClientProgressRecord) =>
    sortCol === col ? (sortAsc ? ' ▲' : ' ▼') : '';

  if (isLoading) {
    return <p style={{ color: '#888' }}>Loading client progress data...</p>;
  }
  if (error) {
    return <p style={{ color: '#c62828' }}>Failed to load client progress data.</p>;
  }
  if (rows.length === 0) {
    return <p style={{ color: '#aaa' }}>No clients with 2+ visits for the selected period.</p>;
  }

  const deltaStyle = (value: number | null, positiveIsGood: boolean): React.CSSProperties => {
    if (value == null || value === 0) {
      return {};
    }
    const isGood = positiveIsGood ? value > 0 : value < 0;
    return { color: isGood ? '#2e7d32' : '#c62828', fontWeight: 600 };
  };

  const formatDelta = (value: number | null) => {
    if (value == null) {
      return '—';
    }
    if (value === 0) {
      return '0';
    }
    return value > 0 ? `+${value}` : `${value}`;
  };

  return (
    <div>
      <p style={{ fontSize: '0.8125rem', color: '#888', marginBottom: '0.75rem' }}>
        {rows.length} client{rows.length !== 1 ? 's' : ''} with 2+ visits — page {page + 1} of{' '}
        {totalPages}
      </p>

      <div className="fd-table-wrap">
        <table className="fd-table">
          <thead>
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('clientUid')}>
                Client ID{sortIndicator('clientUid')}
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('lastName')}>
                Name{sortIndicator('lastName')}
              </th>
              <th>Zip</th>
              <th
                style={{ cursor: 'pointer', textAlign: 'center' }}
                onClick={() => handleSort('visitCount')}
              >
                Visits{sortIndicator('visitCount')}
              </th>
              <th>First Visit</th>
              <th style={{ textAlign: 'center' }}>First USDA</th>
              <th>First Category</th>
              <th style={{ textAlign: 'center' }}>First F/V</th>
              <th>Latest Visit</th>
              <th style={{ textAlign: 'center' }}>Latest USDA</th>
              <th>Latest Category</th>
              <th style={{ textAlign: 'center' }}>Latest F/V</th>
              <th
                style={{ cursor: 'pointer', textAlign: 'center' }}
                onClick={() => handleSort('usdaScoreChange')}
              >
                Score Chg{sortIndicator('usdaScoreChange')}
              </th>
              <th
                style={{ cursor: 'pointer', textAlign: 'center' }}
                onClick={() => handleSort('fruitvegDaysChange')}
              >
                F/V Chg{sortIndicator('fruitvegDaysChange')}
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRecords.map((r) => (
              <tr key={r.fdClientId}>
                <td>{r.clientUid || r.fdClientId}</td>
                <td>
                  {r.firstName} {r.lastName}
                </td>
                <td>{r.zipCode || '—'}</td>
                <td style={{ textAlign: 'center' }}>{r.visitCount}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {r.firstVisitDate ? new Date(r.firstVisitDate).toLocaleDateString() : '—'}
                </td>
                <td style={{ textAlign: 'center' }}>{r.firstUsdaScore ?? '—'}</td>
                <td>{r.firstUsdaCategory || '—'}</td>
                <td style={{ textAlign: 'center' }}>{r.firstFruitvegDays ?? '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {r.latestVisitDate ? new Date(r.latestVisitDate).toLocaleDateString() : '—'}
                </td>
                <td style={{ textAlign: 'center' }}>{r.latestUsdaScore ?? '—'}</td>
                <td>{r.latestUsdaCategory || '—'}</td>
                <td style={{ textAlign: 'center' }}>{r.latestFruitvegDays ?? '—'}</td>
                <td style={{ textAlign: 'center', ...deltaStyle(r.usdaScoreChange, false) }}>
                  {formatDelta(r.usdaScoreChange)}
                </td>
                <td style={{ textAlign: 'center', ...deltaStyle(r.fruitvegDaysChange, true) }}>
                  {formatDelta(r.fruitvegDaysChange)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div
          style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}
        >
          <button
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            style={{
              padding: '0.4rem 0.75rem',
              border: '1px solid #ddd',
              borderRadius: 5,
              background: 'white',
              cursor: page === 0 ? 'default' : 'pointer',
              opacity: page === 0 ? 0.4 : 1,
            }}
          >
            Previous
          </button>
          <span style={{ lineHeight: '2rem', fontSize: '0.875rem', color: '#666' }}>
            {page + 1} / {totalPages}
          </span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
            style={{
              padding: '0.4rem 0.75rem',
              border: '1px solid #ddd',
              borderRadius: 5,
              background: 'white',
              cursor: page >= totalPages - 1 ? 'default' : 'pointer',
              opacity: page >= totalPages - 1 ? 0.4 : 1,
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Operations Tab ────────────────────────────────────────────────────────────

function OperationsTab({ params }: { params: { startDate?: string; endDate?: string } }) {
  const { data: rawData, isLoading, error } = useFDReportsSummary(params);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = rawData as any;

  if (isLoading) {
    return <p style={{ color: '#888' }}>Loading operations data...</p>;
  }
  if (error) {
    return <p style={{ color: '#c62828' }}>Failed to load operations data.</p>;
  }
  if (!data) {
    return <p style={{ color: '#aaa' }}>No data for the selected period.</p>;
  }

  return (
    <div>
      <div className="fd-stat-grid">
        <div className="fd-stat-card">
          <span className="fd-stat-label">Unduplicated Clients</span>
          <span className="fd-stat-value">{data.unduplicatedClients ?? '—'}</span>
          <span className="fd-stat-sub">unique individuals served</span>
        </div>
        <div className="fd-stat-card">
          <span className="fd-stat-label">Total Visits</span>
          <span className="fd-stat-value">{data.totalVisits ?? '—'}</span>
          <span className="fd-stat-sub">distribution interactions</span>
        </div>
        <div className="fd-stat-card">
          <span className="fd-stat-label">New Clients</span>
          <span className="fd-stat-value">{data.newClients ?? '—'}</span>
          <span className="fd-stat-sub">first-time visitors</span>
        </div>
        <div className="fd-stat-card">
          <span className="fd-stat-label">Returning Clients</span>
          <span className="fd-stat-value">{data.returningClients ?? '—'}</span>
          <span className="fd-stat-sub">2nd visit or more</span>
        </div>
      </div>

      {data.visitsByType && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <h4 style={{ marginBottom: '1rem' }}>Visits by Distribution Type</h4>
          <div className="fd-stat-grid">
            {Object.entries(data.visitsByType as Record<string, number>).map(([type, count]) => (
              <div key={type} className="fd-stat-card">
                <span className="fd-stat-label">{type}</span>
                <span className="fd-stat-value">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.pendingReferrals != null && (
        <div className="card">
          <h4 style={{ marginBottom: '0.5rem' }}>Referral Follow-ups</h4>
          <p
            style={{
              fontSize: '1.75rem',
              fontWeight: 700,
              color: data.pendingReferrals > 0 ? '#e65100' : '#2e7d32',
              margin: 0,
            }}
          >
            {data.pendingReferrals}
          </p>
          <p style={{ color: '#888', fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            referrals pending staff follow-up
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Outcomes Tab ──────────────────────────────────────────────────────────────

function OutcomesTab({ params }: { params: { startDate?: string; endDate?: string } }) {
  const { data: rawData, isLoading, error } = useFDReportsOutcomes(params);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = rawData as any;

  if (isLoading) {
    return <p style={{ color: '#888' }}>Loading outcomes data...</p>;
  }
  if (error) {
    return <p style={{ color: '#c62828' }}>Failed to load outcomes data.</p>;
  }
  if (!data) {
    return <p style={{ color: '#aaa' }}>No outcomes data for the selected period.</p>;
  }

  const foodInsecPct =
    data.foodInsecurityChange != null
      ? `${data.foodInsecurityChange > 0 ? '+' : ''}${data.foodInsecurityChange.toFixed(1)}%`
      : null;

  const fruitvegPct =
    data.fruitvegChange != null
      ? `${data.fruitvegChange > 0 ? '+' : ''}${data.fruitvegChange.toFixed(1)}%`
      : null;

  const engagePct = data.engagementRate != null ? `${data.engagementRate.toFixed(1)}%` : null;

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        {/* Outcome 1: Food Insecurity */}
        <div className="fd-outcome-card">
          <h4>Food Insecurity Reduction (Target: 10%)</h4>
          <div className="fd-outcome-big">{foodInsecPct ?? '—'}</div>
          {data.foodInsecurityChange != null && (
            <div
              className={`fd-outcome-change ${data.foodInsecurityChange <= -10 ? 'positive' : 'negative'}`}
            >
              {data.foodInsecurityChange <= -10 ? '✓ Target met' : '↗ Target: −10%'}
            </div>
          )}
          {data.pctLowVeryLowFirst != null && data.pctLowVeryLowLatest != null && (
            <>
              <div className="fd-target-bar">
                <div
                  className="fd-target-fill"
                  style={{
                    width: `${Math.min(100, (Math.abs(data.foodInsecurityChange ?? 0) / 10) * 100)}%`,
                    background: (data.foodInsecurityChange ?? 0) <= -10 ? '#4CAF50' : '#5B2C86',
                  }}
                />
              </div>
              <div className="fd-target-label">
                <span>First visits: {data.pctLowVeryLowFirst.toFixed(0)}% low/very low</span>
                <span>Latest: {data.pctLowVeryLowLatest.toFixed(0)}%</span>
              </div>
            </>
          )}
        </div>

        {/* Outcome 2: Fruit & Veg */}
        <div className="fd-outcome-card">
          <h4>Fruit & Vegetable Increase (Target: 20%)</h4>
          <div className="fd-outcome-big">{fruitvegPct ?? '—'}</div>
          {data.fruitvegChange != null && (
            <div
              className={`fd-outcome-change ${data.fruitvegChange >= 20 ? 'positive' : 'negative'}`}
            >
              {data.fruitvegChange >= 20 ? '✓ Target met' : '↗ Target: +20%'}
            </div>
          )}
          {data.avgFruitvegFirst != null && data.avgFruitvegLatest != null && (
            <>
              <div className="fd-target-bar">
                <div
                  className="fd-target-fill"
                  style={{
                    width: `${Math.min(100, ((data.fruitvegChange ?? 0) / 20) * 100)}%`,
                    background: (data.fruitvegChange ?? 0) >= 20 ? '#4CAF50' : '#5B2C86',
                  }}
                />
              </div>
              <div className="fd-target-label">
                <span>First: {data.avgFruitvegFirst.toFixed(1)} days/wk</span>
                <span>Latest: {data.avgFruitvegLatest.toFixed(1)} days/wk</span>
              </div>
            </>
          )}
        </div>

        {/* Outcome 3: Wraparound engagement */}
        <div className="fd-outcome-card">
          <h4>Wraparound Engagement (Target: 40%)</h4>
          <div className="fd-outcome-big">{engagePct ?? '—'}</div>
          {data.engagementRate != null && (
            <div
              className={`fd-outcome-change ${data.engagementRate >= 40 ? 'positive' : 'negative'}`}
            >
              {data.engagementRate >= 40 ? '✓ Target met' : '↗ Target: 40%'}
            </div>
          )}
          {data.totalReferrals != null && data.engagedReferrals != null && (
            <>
              <div className="fd-target-bar">
                <div
                  className="fd-target-fill"
                  style={{
                    width: `${Math.min(100, ((data.engagementRate ?? 0) / 40) * 100)}%`,
                    background: (data.engagementRate ?? 0) >= 40 ? '#4CAF50' : '#5B2C86',
                  }}
                />
              </div>
              <div className="fd-target-label">
                <span>{data.engagedReferrals} clients engaged</span>
                <span>{data.totalReferrals} clients referred</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Average USDA score table */}
      {data.avgScoreByMonth && (
        <div className="card">
          <h4 style={{ marginBottom: '1rem' }}>Average USDA Score by Month</h4>
          <div className="fd-table-wrap">
            <table className="fd-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Avg USDA Score</th>
                  <th>Visits</th>
                  <th>Avg Fruit/Veg Days</th>
                </tr>
              </thead>
              <tbody>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {(data.avgScoreByMonth as any[]).map((row: any) => (
                  <tr key={row.month}>
                    <td>{row.month}</td>
                    <td>{row.avgScore?.toFixed(2) ?? '—'}</td>
                    <td>{row.visitCount ?? '—'}</td>
                    <td>{row.avgFruitveg?.toFixed(1) ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Service Needs Tab ────────────────────────────────────────────────────────

function ServiceNeedsTab({ params }: { params: { startDate?: string; endDate?: string } }) {
  const { data: rawData, isLoading, error } = useFDReportsServiceNeeds(params);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = rawData as any;

  if (isLoading) {
    return <p style={{ color: '#888' }}>Loading service needs data...</p>;
  }
  if (error) {
    return <p style={{ color: '#c62828' }}>Failed to load service needs data.</p>;
  }
  if (!data?.serviceNeeds?.length) {
    return <p style={{ color: '#aaa' }}>No service needs data for the selected period.</p>;
  }

  const needs: { service: string; count: number; uniqueClients: number }[] =
    data.serviceNeeds ?? [];
  const maxCount = Math.max(...needs.map((n) => n.count), 1);
  const totalReferrals: number = data.totalReferrals ?? needs.reduce((s, n) => s + n.count, 0);
  const totalClientsWithNeeds: number = data.totalClientsWithNeeds ?? 0;
  const avgPerClient =
    totalClientsWithNeeds > 0 ? (totalReferrals / totalClientsWithNeeds).toFixed(1) : '—';
  const topService = needs.length > 0 ? needs[0].service : '—';

  // Color palette for the bar chart
  const barColors = [
    '#5B2C86',
    '#7B3FA0',
    '#9B52BA',
    '#FAA31B',
    '#E89A1B',
    '#D08A15',
    '#3E7B9E',
    '#4A9BBF',
    '#2E7D32',
    '#9D9FA1',
    '#6D4C9E',
  ];

  return (
    <div>
      {/* Summary stat cards */}
      <div className="fd-stat-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="fd-stat-card">
          <span className="fd-stat-label">Total Requests</span>
          <span className="fd-stat-value">{totalReferrals}</span>
          <span className="fd-stat-sub">service need selections</span>
        </div>
        <div className="fd-stat-card">
          <span className="fd-stat-label">Clients with Needs</span>
          <span className="fd-stat-value">{totalClientsWithNeeds}</span>
          <span className="fd-stat-sub">unique clients requesting support</span>
        </div>
        <div className="fd-stat-card">
          <span className="fd-stat-label">Avg per Client</span>
          <span className="fd-stat-value">{avgPerClient}</span>
          <span className="fd-stat-sub">services selected per client</span>
        </div>
        <div className="fd-stat-card">
          <span className="fd-stat-label">Top Service Need</span>
          <span className="fd-stat-value" style={{ fontSize: '1.25rem' }}>
            {topService}
          </span>
          <span className="fd-stat-sub">most requested service</span>
        </div>
      </div>

      {/* Horizontal bar chart */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1.25rem' }}>
        <h4 style={{ margin: '0 0 1rem 0', color: '#5B2C86', fontSize: '0.9375rem' }}>
          Service Needs Distribution
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {needs.map((n, i) => {
            const pct = (n.count / totalReferrals) * 100;
            return (
              <div key={n.service}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.25rem',
                  }}
                >
                  <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#333' }}>
                    {n.service}
                  </span>
                  <span style={{ fontSize: '0.8125rem', color: '#666' }}>
                    {n.count} ({pct.toFixed(0)}%)
                  </span>
                </div>
                <div
                  style={{ height: 20, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${(n.count / maxCount) * 100}%`,
                      background: barColors[i % barColors.length],
                      borderRadius: 4,
                      transition: 'width 0.4s ease',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail table */}
      <div className="fd-table-wrap" style={{ marginBottom: '1.25rem' }}>
        <table className="fd-table">
          <thead>
            <tr>
              <th style={{ width: 40, textAlign: 'center' }}>#</th>
              <th>Service</th>
              <th style={{ textAlign: 'center' }}>Selections</th>
              <th style={{ textAlign: 'center' }}>Unique Clients</th>
              <th>% of Total</th>
            </tr>
          </thead>
          <tbody>
            {needs.map((n, i) => (
              <tr key={n.service}>
                <td style={{ textAlign: 'center', fontWeight: 600, color: '#888' }}>{i + 1}</td>
                <td style={{ fontWeight: 500 }}>{n.service}</td>
                <td style={{ textAlign: 'center' }}>{n.count}</td>
                <td style={{ textAlign: 'center' }}>{n.uniqueClients}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div
                      style={{
                        flex: 1,
                        height: 8,
                        background: '#eee',
                        borderRadius: 4,
                        overflow: 'hidden',
                        minWidth: 80,
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${(n.count / totalReferrals) * 100}%`,
                          background: barColors[i % barColors.length],
                          borderRadius: 4,
                        }}
                      />
                    </div>
                    <span style={{ fontSize: '0.8125rem', color: '#888', minWidth: 32 }}>
                      {((n.count / totalReferrals) * 100).toFixed(0)}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function FDReportsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('detail');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  const reportParams = {
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      if (activeTab === 'detail') {
        // Export full visit detail records
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const exportData = (await getFDReportsExport(reportParams)) as any;
        const records = exportData?.records ?? exportData;
        if (Array.isArray(records) && records.length > 0) {
          exportToCsv(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            records.map((r: any) => ({
              ...r,
              serviceNeeds: parseServiceNeeds(r.serviceNeeds),
              usdaAd1aDontknow: r.usdaAd1aDontknow ? 'Yes' : '',
              fruitvegDontknow: r.fruitvegDontknow ? 'Yes' : '',
            })),
            `fd-visit-detail-${csvFilenameDate()}`,
            [
              { key: 'visitDatetime', label: 'Date' },
              { key: 'clientUid', label: 'Client ID' },
              { key: 'firstName', label: 'First Name' },
              { key: 'lastName', label: 'Last Name' },
              { key: 'zipCode', label: 'Zip Code' },
              { key: 'householdSize', label: 'Household Size' },
              { key: 'childrenInHousehold', label: 'Children in Household' },
              { key: 'tefapEligibility', label: 'TEFAP Eligibility' },
              { key: 'distributionType', label: 'Distribution Type' },
              {
                key: 'usdaHh3',
                label:
                  "The food that I/we bought just didn't last, and I/we didn't have money to get more.",
              },
              { key: 'usdaHh4', label: "I/we couldn't afford to eat balanced meals." },
              {
                key: 'usdaAd1',
                label:
                  "Did you or other adults ever cut the size of your meals or skip meals because there wasn't enough money for food?",
              },
              { key: 'usdaAd1a', label: 'How many days did this happen? (0-30)' },
              { key: 'usdaAd1aDontknow', label: "How many days - Don't know/prefer not to say" },
              {
                key: 'usdaAd2',
                label:
                  "Did you ever eat less than you felt you should because there wasn't enough money for food?",
              },
              {
                key: 'usdaAd3',
                label:
                  "Were you ever hungry but didn't eat because there wasn't enough money for food?",
              },
              { key: 'usda6RawScore', label: 'USDA Score (0-6)' },
              { key: 'usda6Category', label: 'USDA Category' },
              {
                key: 'fruitvegDays',
                label:
                  'In the last 7 days, on how many days did you eat at least 1 serving of fruits or vegetables?',
              },
              { key: 'fruitvegDontknow', label: "Fruit/Veg Days - Don't know/prefer not to say" },
              { key: 'serviceNeeds', label: 'Service Needs' },
              { key: 'staffUser', label: 'Staff' },
              { key: 'notes', label: 'Notes' },
            ]
          );
        } else {
          alert('No visit records to export for the selected period.');
        }
        return;
      }

      if (activeTab === 'progress') {
        const progressData = await getFDReportsProgress(reportParams);
        if (Array.isArray(progressData) && progressData.length > 0) {
          exportToCsv(
            progressData as unknown as Record<string, unknown>[],
            `fd-client-progress-${csvFilenameDate()}`,
            [
              { key: 'clientUid', label: 'Client ID' },
              { key: 'firstName', label: 'First Name' },
              { key: 'lastName', label: 'Last Name' },
              { key: 'zipCode', label: 'Zip Code' },
              { key: 'householdSize', label: 'Household Size' },
              { key: 'visitCount', label: 'Total Visits' },
              { key: 'firstVisitDate', label: 'First Visit Date' },
              { key: 'firstUsdaScore', label: 'First USDA Score' },
              { key: 'firstUsdaCategory', label: 'First USDA Category' },
              { key: 'firstFruitvegDays', label: 'First Fruit/Veg Days' },
              { key: 'latestVisitDate', label: 'Latest Visit Date' },
              { key: 'latestUsdaScore', label: 'Latest USDA Score' },
              { key: 'latestUsdaCategory', label: 'Latest USDA Category' },
              { key: 'latestFruitvegDays', label: 'Latest Fruit/Veg Days' },
              { key: 'usdaScoreChange', label: 'USDA Score Change' },
              { key: 'fruitvegDaysChange', label: 'Fruit/Veg Days Change' },
            ]
          );
        } else {
          alert('No client progress data to export for the selected period.');
        }
        return;
      }

      // Choose export data source based on active tab
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let rawData: any;
      if (activeTab === 'operations') {
        rawData = await getFDReportsSummary(reportParams);
      } else if (activeTab === 'outcomes') {
        rawData = await getFDReportsOutcomes(reportParams);
      } else {
        rawData = await getFDReportsServiceNeeds(reportParams);
      }

      // Try the dedicated export endpoint first
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const exportData = (await getFDReportsExport(reportParams)) as any[];
        if (Array.isArray(exportData) && exportData.length > 0) {
          exportToCsv(exportData, `fd-export-${csvFilenameDate()}`);
          return;
        }
      } catch {
        // fall through to inline export
      }

      // Inline export from the current tab's data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (activeTab === 'service-needs' && (rawData as any)?.serviceNeeds) {
        exportToCsv(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (rawData as any).serviceNeeds,
          `fd-service-needs-${csvFilenameDate()}`,
          [
            { key: 'service', label: 'Service' },
            { key: 'count', label: 'Selections' },
            { key: 'uniqueClients', label: 'Unique Clients' },
          ]
        );
      } else {
        // Flatten whatever we got
        const flat = Array.isArray(rawData) ? rawData : [rawData];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        exportToCsv(flat as any[], `fd-report-${csvFilenameDate()}`);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      alert('Export failed: ' + (err.message || 'Unknown error'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '0.75rem' }}>
        <Link to="/food-distribution" className="fd-table-link">
          ← Back to Food Distribution
        </Link>
      </div>

      <div className="page-header">
        <h2>Food Distribution Reports</h2>
        <p>Outcome tracking, compliance metrics, and export for DOA monthly activity reports</p>
      </div>

      {/* Filter bar */}
      <div className="fd-filter-bar" style={{ marginBottom: '1.25rem' }}>
        <label>
          Start Date
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label>
          End Date
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
          <button
            type="button"
            onClick={() => {
              const now = new Date();
              const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
              setStartDate(firstOfMonth.toISOString().slice(0, 10));
              setEndDate(now.toISOString().slice(0, 10));
            }}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid #ddd',
              borderRadius: 5,
              background: 'white',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontFamily: 'Montserrat, sans-serif',
            }}
          >
            This Month
          </button>
          <button
            type="button"
            onClick={() => {
              setStartDate('');
              setEndDate('');
            }}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid #ddd',
              borderRadius: 5,
              background: 'white',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontFamily: 'Montserrat, sans-serif',
            }}
          >
            All Time
          </button>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button className="fd-export-btn" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Exporting...' : '↓ Export CSV'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="fd-tabs">
        <TabButton
          id="detail"
          label="Detail"
          active={activeTab === 'detail'}
          onClick={() => setActiveTab('detail')}
        />
        <TabButton
          id="progress"
          label="Client Progress"
          active={activeTab === 'progress'}
          onClick={() => setActiveTab('progress')}
        />
        <TabButton
          id="operations"
          label="Operations"
          active={activeTab === 'operations'}
          onClick={() => setActiveTab('operations')}
        />
        <TabButton
          id="outcomes"
          label="Outcomes"
          active={activeTab === 'outcomes'}
          onClick={() => setActiveTab('outcomes')}
        />
        <TabButton
          id="service-needs"
          label="Service Needs"
          active={activeTab === 'service-needs'}
          onClick={() => setActiveTab('service-needs')}
        />
      </div>

      <div>
        {activeTab === 'detail' && <DetailTab params={reportParams} />}
        {activeTab === 'progress' && <ProgressTab params={reportParams} />}
        {activeTab === 'operations' && <OperationsTab params={reportParams} />}
        {activeTab === 'outcomes' && <OutcomesTab params={reportParams} />}
        {activeTab === 'service-needs' && <ServiceNeedsTab params={reportParams} />}
      </div>
    </div>
  );
}
